/**
 * A lightweight HTTP client using native fetch API.
 * Provides a similar interface to axios for easier migration.
 */

/**
 * Makes an HTTP request using native fetch.
 *
 * @param {string} url - The URL to request
 * @param {Object} options - Request options
 * @param {string} [options.method='GET'] - HTTP method
 * @param {Object} [options.headers] - Request headers
 * @param {Object} [options.params] - URL query parameters
 * @param {Object|string} [options.data] - Request body (for POST/PUT/PATCH)
 * @param {boolean} [options.withCredentials=false] - Include credentials
 * @param {AbortSignal} [options.signal] - AbortController signal for cancellation
 * @returns {Promise<{data: any, status: number, headers: Headers}>}
 */
const request = async (url, options = {}) => {
    const {
        method = 'GET',
        headers = {},
        params,
        data,
        withCredentials = false,
        signal,
    } = options;

    // Build URL with query parameters
    let fullUrl = url;
    if (params) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                searchParams.append(key, String(value));
            }
        });
        const queryString = searchParams.toString();
        if (queryString) {
            fullUrl = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
        }
    }

    // Prepare fetch options
    const fetchOptions = {
        method,
        headers: { ...headers },
        credentials: withCredentials ? 'include' : 'same-origin',
        signal,
    };

    // Add body for non-GET requests
    if (data && method !== 'GET') {
        if (typeof data === 'object') {
            fetchOptions.headers['Content-Type'] = 'application/json';
            fetchOptions.body = JSON.stringify(data);
        } else {
            fetchOptions.body = data;
        }
    }

    const response = await fetch(fullUrl, fetchOptions);

    // Parse response body
    let responseData;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
    } else {
        responseData = await response.text();
    }

    // Throw error for non-2xx responses (similar to axios behavior)
    if (!response.ok) {
        const error = new Error(`Request failed with status ${response.status}`);
        error.response = {
            data: responseData,
            status: response.status,
            headers: response.headers,
        };
        error.message = responseData?.message || error.message;
        throw error;
    }

    return {
        data: responseData,
        status: response.status,
        headers: response.headers,
    };
};

/**
 * Makes a GET request.
 *
 * @param {string} url - The URL to request
 * @param {Object} [options] - Request options (headers, params, etc.)
 * @returns {Promise<{data: any, status: number, headers: Headers}>}
 */
export const get = (url, options = {}) => request(url, { ...options, method: 'GET' });

/**
 * Makes a POST request.
 *
 * @param {string} url - The URL to request
 * @param {Object|string} [data] - Request body
 * @param {Object} [options] - Request options (headers, params, etc.)
 * @returns {Promise<{data: any, status: number, headers: Headers}>}
 */
export const post = (url, data, options = {}) => request(url, { ...options, method: 'POST', data });

/**
 * Makes a PUT request.
 *
 * @param {string} url - The URL to request
 * @param {Object|string} [data] - Request body
 * @param {Object} [options] - Request options (headers, params, etc.)
 * @returns {Promise<{data: any, status: number, headers: Headers}>}
 */
export const put = (url, data, options = {}) => request(url, { ...options, method: 'PUT', data });

/**
 * Makes a DELETE request.
 *
 * @param {string} url - The URL to request
 * @param {Object} [options] - Request options (headers, params, etc.)
 * @returns {Promise<{data: any, status: number, headers: Headers}>}
 */
export const del = (url, options = {}) => request(url, { ...options, method: 'DELETE' });

export default {
    get,
    post,
    put,
    delete: del,
    request,
};
